/**
 * OS Window — draggable, resizable (sağ alt köşe), closeable, minimizable, maximizable, pinnable
 */
import { Maximize2, Minus, Pin, PinOff, Settings, X } from 'lucide-react'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useResponsive } from '../../../hooks/useResponsive'
import ErrorBoundaryOS from '../ErrorBoundaryOS'
import { useWindowManager } from './WindowManagerContext'
import type { OSWindowState } from './types'

interface Props {
  win: OSWindowState
}

type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'left-half' | 'right-half'

function SnapZoneOverlay({ isDragging, activeCorner }: { isDragging: boolean; activeCorner: Corner | null }) {
  if (!isDragging) return null
  const zones: { id: Corner; top?: number; bottom?: number; left?: number; right?: number }[] = [
    { id: 'top-left', top: 0, left: 0 },
    { id: 'top-right', top: 0, right: 0 },
    { id: 'bottom-left', bottom: 0, left: 0 },
    { id: 'bottom-right', bottom: 0, right: 0 },
  ]
  return (
    <>
      {zones.map(z => (
        <div key={z.id} style={{
          position: 'fixed', width: 100, height: 100, zIndex: 9990,
          top: z.top, bottom: z.bottom, left: z.left, right: z.right,
          border: `2px dashed ${activeCorner === z.id ? 'var(--os-accent)' : 'var(--os-border)'}`,
          background: activeCorner === z.id ? 'var(--os-accent-bg)' : 'transparent',
          opacity: activeCorner === z.id ? 0.6 : 0.3,
          borderRadius: 8, pointerEvents: 'none',
          transition: 'opacity 0.15s, background 0.15s, border-color 0.15s',
        }} />
      ))}
    </>
  )
}

const PIN_CORNERS = [
  { id: 'top-left' as const,     label: 'Top Left',     x: 12,    y: 50,  align: 'flex-start' },
  { id: 'top-right' as const,    label: 'Top Right',    x: -12,   y: 50,  align: 'flex-end' },
  { id: 'bottom-left' as const,  label: 'Bottom Left',  x: 12,    y: -12, align: 'flex-start' },
  { id: 'bottom-right' as const, label: 'Bottom Right', x: -12,   y: -12, align: 'flex-end' },
]

type ResizeDir = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw'

const RESIZE_HANDLES: { dir: ResizeDir; cursor: string; style: React.CSSProperties }[] = [
  { dir: 'n',  cursor: 'ns-resize',   style: { top: 0, left: 8, right: 8, height: 4 } },
  { dir: 's',  cursor: 'ns-resize',   style: { bottom: 0, left: 8, right: 8, height: 4 } },
  { dir: 'e',  cursor: 'ew-resize',   style: { top: 8, right: 0, bottom: 8, width: 4 } },
  { dir: 'w',  cursor: 'ew-resize',   style: { top: 8, left: 0, bottom: 8, width: 4 } },
  { dir: 'ne', cursor: 'nesw-resize', style: { top: 0, right: 0, width: 8, height: 8 } },
  { dir: 'nw', cursor: 'nwse-resize', style: { top: 0, left: 0, width: 8, height: 8 } },
  { dir: 'se', cursor: 'nwse-resize', style: { bottom: 0, right: 0, width: 8, height: 8 } },
  { dir: 'sw', cursor: 'nesw-resize', style: { bottom: 0, left: 0, width: 8, height: 8 } },
]

export default function OSWindow({ win }: Props) {
  const { closeWindow, focusWindow, minimizeWindow, maximizeWindow, restoreWindow, updatePosition, updateSize, pinWindow, unpinWindow, activeWindowId } = useWindowManager()
  const { isMobile } = useResponsive()
  const isFocused = activeWindowId === win.id
  const [showPinMenu, setShowPinMenu] = useState(false)
  const [isHoveringPinned, setIsHoveringPinned] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [activeCorner, setActiveCorner] = useState<Corner | null>(null)
  const [snapPreview, setSnapPreview] = useState<'left' | 'right' | 'max' | null>(null)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const resizeRef = useRef<{ startX: number; startY: number; origX: number; origY: number; origW: number; origH: number; dir: ResizeDir } | null>(null)
  const preMaxRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null)
  const Component = win.component
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    // Global flag — DesktopHero checks this to avoid double menu
    ;(window as any).__runai_window_context_menu = true
    setTimeout(() => { (window as any).__runai_window_context_menu = false }, 50)
    focusWindow(win.id)
    setContextMenu({ x: e.clientX, y: e.clientY })
  }

  const handleContextAction = (action: 'agent' | 'summarize' | 'newWindow') => {
    if (action === 'agent') {
      window.dispatchEvent(new CustomEvent('runai:window-context', { detail: { windowId: win.id, action: 'sendToChat' } }))
    } else if (action === 'summarize') {
      window.dispatchEvent(new CustomEvent('runai:window-context', { detail: { windowId: win.id, action: 'summarize' } }))
    } else if (action === 'newWindow') {
      window.dispatchEvent(new CustomEvent('runai:window-context', { detail: { windowId: win.id, action: 'duplicate' } }))
    }
    setContextMenu(null)
  }

  // Detect corner snap — pencereyi köşeye sürükleyince otomatik pin yap
  const detectCornerPin = (x: number, y: number): NonNullable<typeof win.pinned> | null => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const threshold = 100
    const isLeft = x < threshold
    const isRight = x > vw - threshold
    const isTop = y < threshold
    const isBottom = y > vh - threshold

    if (isLeft && isTop) return 'top-left'
    if (isRight && isTop) return 'top-right'
    if (isLeft && isBottom) return 'bottom-left'
    if (isRight && isBottom) return 'bottom-right'
    return null
  }

  // Detect edge snap — kenar yapışma (Win11 style half-snap)
  const detectEdgeSnap = (x: number, y: number): NonNullable<typeof win.pinned> | null => {
    const vw = window.innerWidth
    if (x < 30 && y > 50) return 'left-half'
    if (x > vw - 30) return 'right-half'
    return null
  }

  // Combined snap detection: edge takes priority over corner
  const detectSnap = (x: number, y: number): NonNullable<typeof win.pinned> | null => {
    return detectEdgeSnap(x, y) || detectCornerPin(x, y)
  }

  // Double-click title bar → maximize/restore toggle (pre-state restore)
  const handleMaximizeToggle = useCallback(() => {
    if (isMobile) return
    if (win.maximized) {
      // Restore pre-maximize state
      if (preMaxRef.current) {
        updatePosition(win.id, { x: preMaxRef.current.x, y: preMaxRef.current.y })
        updateSize(win.id, { width: preMaxRef.current.w, height: preMaxRef.current.h })
      }
      restoreWindow(win.id)
    } else {
      // Save current state before maximize
      preMaxRef.current = { x: win.position.x, y: win.position.y, w: win.size.width, h: win.size.height }
      maximizeWindow(win.id)
    }
  }, [win.id, win.maximized, win.position, win.size, isMobile, restoreWindow, maximizeWindow, updatePosition, updateSize])

  const onTitleDoubleClick = handleMaximizeToggle

  // Drag via title bar (pointer events — KURAL 70)
  const onTitlePointerDown = useCallback((e: React.PointerEvent) => {
    if (isMobile || win.maximized || win.pinned) { return }
    e.preventDefault()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    focusWindow(win.id)
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: win.position.x, origY: win.position.y }
    setIsDragging(true)
    setActiveCorner(null)
    setSnapPreview(null)

    let lastPosition = { x: win.position.x, y: win.position.y }

    const onMove = (ev: PointerEvent) => {
      if (!dragRef.current) { return }
      lastPosition = {
        x: Math.max(0, dragRef.current.origX + ev.clientX - dragRef.current.startX),
        y: Math.max(0, dragRef.current.origY + ev.clientY - dragRef.current.startY),
      }
      updatePosition(win.id, lastPosition)
      setActiveCorner(detectSnap(lastPosition.x, lastPosition.y))
      // Edge snap preview
      if (ev.clientX < 30) setSnapPreview('left')
      else if (ev.clientX > window.innerWidth - 30) setSnapPreview('right')
      else if (ev.clientY < 30) setSnapPreview('max')
      else setSnapPreview(null)
    }
    const onUp = () => {
      const snap = detectSnap(lastPosition.x, lastPosition.y)
      if (snap) { pinWindow(win.id, snap) }
      dragRef.current = null
      setIsDragging(false)
      setActiveCorner(null)
      setSnapPreview(null)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [win, focusWindow, updatePosition, pinWindow])

  // 8-directional resize via pointer events (KURAL 70)
  const onResizePointerDown = useCallback((dir: ResizeDir, e: React.PointerEvent) => {
    if (isMobile || win.maximized || win.pinned) { return }
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    focusWindow(win.id)
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origX: win.position.x, origY: win.position.y, origW: win.size.width, origH: win.size.height, dir }

    const onMove = (ev: PointerEvent) => {
      if (!resizeRef.current) return
      const { startX, startY, origX, origY, origW, origH, dir: d } = resizeRef.current
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      let x = origX, y = origY, w = origW, h = origH

      if (d.includes('e')) w = Math.max(320, origW + dx)
      if (d.includes('s')) h = Math.max(240, origH + dy)
      if (d.includes('w')) { w = Math.max(320, origW - dx); x = origX + origW - w }
      if (d === 'n' || d === 'nw' || d === 'ne') { h = Math.max(240, origH - dy); y = origY + origH - h }

      updateSize(win.id, { width: w, height: h })
      updatePosition(win.id, { x, y })
    }
    const onUp = () => { resizeRef.current = null; window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp) }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }, [win, focusWindow, updateSize, updatePosition])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { closeWindow(win.id) } }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [win.id, closeWindow])

  if (win.minimized) { return null }

  const pinnedStyle = (corner: NonNullable<typeof win.pinned>): React.CSSProperties => {
    if (corner === 'left-half')  return { position: 'absolute', left: 0, top: 34, width: '50%', height: 'calc(100% - 34px)' }
    if (corner === 'right-half') return { position: 'absolute', right: 0, top: 34, width: '50%', height: 'calc(100% - 34px)' }
    const w = Math.min(win.size.width, 380)
    const h = Math.min(win.size.height, 320)
    if (corner === 'top-left')     return { position: 'absolute', top: 50,  left: 12,  width: w, height: h }
    if (corner === 'top-right')    return { position: 'absolute', top: 50,  right: 12, width: w, height: h }
    if (corner === 'bottom-left')  return { position: 'absolute', bottom: 12, left: 12,  width: w, height: h }
    return                                 { position: 'absolute', bottom: 12, right: 12, width: w, height: h }
  }

  const style: React.CSSProperties = isMobile
    ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', borderRadius: 0 }
    : win.maximized
    ? { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', borderRadius: 0 }
    : win.pinned
    ? pinnedStyle(win.pinned)
    : { position: 'absolute', top: win.position.y, left: win.position.x, width: win.size.width, height: win.size.height }

  return (
    <div
      data-window-id={win.id}
      data-focused={isFocused ? 'true' : 'false'}
      role="dialog"
      aria-label={win.title}
      aria-modal="false"
      onClick={() => focusWindow(win.id)}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => win.pinned && setIsHoveringPinned(true)}
      onMouseLeave={() => { setIsHoveringPinned(false); setContextMenu(null) }}
      style={{
        ...style,
        background: 'var(--os-win-bg)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: `1px solid ${isFocused ? 'var(--os-border-strong)' : 'var(--os-border)'}`,
        borderRadius: win.maximized ? 0 : '12px',
        boxShadow: win.pinned && isHoveringPinned
          ? 'var(--os-elevation-3)'
          : isFocused
            ? 'var(--os-win-shadow-focus)'
            : 'var(--os-win-shadow)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: win.zIndex,
        animation: win.pinned && isHoveringPinned ? 'none' : 'winIn .22s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transform: win.pinned && isHoveringPinned ? 'scale(1.08)' : 'scale(1)',
        transition: win.pinned ? 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'box-shadow 0.18s ease',
        transformOrigin: win.pinned === 'bottom-left' ? 'left bottom' :
                         win.pinned === 'bottom-right' ? 'right bottom' :
                         win.pinned === 'top-left' ? 'left top' :
                         win.pinned === 'top-right' ? 'right top' :
                         win.pinned === 'left-half' ? 'left center' :
                         win.pinned === 'right-half' ? 'right center' : 'center',
      }}
    >
      <style>{`
        @keyframes winIn {
          from { transform: scale(0.92); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
      `}</style>

      {/* Title Bar — Drag Area (KURAL: kullanıcı nereyi tutacağını anlamalı) */}
      <div
        onPointerDown={onTitlePointerDown}
        onDoubleClick={onTitleDoubleClick}
        onMouseEnter={(e) => {
          if (!win.maximized && !win.pinned) {
            (e.currentTarget as HTMLElement).style.background = 'var(--os-win-tb-hover)'
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--os-win-tb)'
        }}
        title={win.maximized || win.pinned ? '' : 'Drag to move window'}
        style={{
          display: 'flex', alignItems: 'center',
          height: '36px', padding: '0 12px',
          background: 'var(--os-win-tb)',
          borderBottom: '1px solid var(--os-win-tb-border)',
          cursor: isMobile || win.maximized || win.pinned ? 'default' : 'grab',
          flexShrink: 0, userSelect: 'none',
          transition: 'background 0.12s ease',
          position: 'relative',
        }}
      >
        {/* Traffic lights (close/min/max) */}
        <div style={{ display: 'flex', gap: '6px', marginRight: '12px', alignItems: 'center' }}>
          <button onClick={e => { e.stopPropagation(); closeWindow(win.id) }}
            aria-label="Close window"
            style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff5f57', border: '0.5px solid rgba(0,0,0,0.15)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={7} color="rgba(0,0,0,0.5)" strokeWidth={2.5} />
          </button>
          <button onClick={e => { e.stopPropagation(); minimizeWindow(win.id) }}
            aria-label="Minimize window"
            style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#febc2e', border: '0.5px solid rgba(0,0,0,0.15)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Minus size={7} color="rgba(0,0,0,0.5)" strokeWidth={2.5} />
          </button>
          <button onClick={e => { e.stopPropagation(); handleMaximizeToggle() }}
            aria-label="Maximize window"
            style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#28c840', border: '0.5px solid rgba(0,0,0,0.15)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Maximize2 size={7} color="rgba(0,0,0,0.5)" strokeWidth={2.5} />
          </button>
        </div>

        {/* Drag handle indicator (dots) — sürükleme alanı görsel ipucu */}
        {!isMobile && !win.maximized && !win.pinned && (
          <div
            aria-hidden="true"
            style={{
              display: 'flex', alignItems: 'center', gap: 3,
              opacity: 0.35, marginRight: 8, pointerEvents: 'none',
            }}>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
          </div>
        )}

        {/* Title */}
        <div style={{
          flex: 1, textAlign: 'center',
          fontSize: '12px', fontWeight: 600,
          color: 'var(--os-win-tb-text)',
          letterSpacing: '0.2px', pointerEvents: 'none',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {win.title}
          {win.pinned && <span style={{ color: 'var(--os-accent)', marginLeft: 6, fontSize: 10, fontWeight: 500 }}>· pinned</span>}
        </div>

        {/* Drag handle indicator (right side mirror) */}
        {!isMobile && !win.maximized && !win.pinned && (
          <div
            aria-hidden="true"
            style={{
              display: 'flex', alignItems: 'center', gap: 3,
              opacity: 0.35, marginLeft: 8, marginRight: 4, pointerEvents: 'none',
            }}>
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--os-win-tb-text)' }} />
          </div>
        )}

        {/* Pin button + menu */}
        {!isMobile && <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Settings button — opens window-specific settings */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              window.dispatchEvent(new CustomEvent('runai:window-settings', { detail: { windowId: win.id } }))
            }}
            title="Window settings"
            style={{
              width: 22, height: 22, borderRadius: 5,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--os-muted)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--os-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <Settings size={11} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              if (win.pinned) { unpinWindow(win.id) }
              else { setShowPinMenu(p => !p) }
            }}
            title={win.pinned ? 'Unpin' : 'Pin to corner'}
            style={{
              width: 22, height: 22, borderRadius: 5,
              background: win.pinned ? 'rgba(99,102,241,0.18)' : 'transparent',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: win.pinned ? 'var(--os-accent)' : 'var(--os-muted)',
            }}
            onMouseEnter={e => { if (!win.pinned) e.currentTarget.style.background = 'var(--os-hover)' }}
            onMouseLeave={e => { if (!win.pinned) e.currentTarget.style.background = 'transparent' }}
          >
            {win.pinned ? <PinOff size={11} /> : <Pin size={11} />}
          </button>
          {showPinMenu && !win.pinned && (
            <>
              <div onClick={() => setShowPinMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
              <div style={{
                position: 'absolute', top: 28, right: 0,
                minWidth: 140,
                background: 'var(--os-surface)',
                border: '1px solid var(--os-border)',
                borderRadius: 8,
                padding: 4,
                boxShadow: 'var(--os-card-shadow)',
                zIndex: 9999,
              }}>
                <div style={{ fontSize: 9, color: 'var(--os-muted)', padding: '4px 8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Pin to corner
                </div>
                {PIN_CORNERS.map(c => (
                  <button
                    key={c.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      pinWindow(win.id, c.id)
                      setShowPinMenu(false)
                    }}
                    style={{
                      width: '100%', padding: '6px 10px',
                      background: 'transparent', border: 'none',
                      color: 'var(--os-text)', fontSize: 11,
                      cursor: 'pointer', borderRadius: 5,
                      textAlign: 'left', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--os-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>}
      </div>

      {/* Content */}
      <div style={{
        flex: 1, overflow: 'auto', position: 'relative',
        background: 'var(--os-win-bg)',
        color: 'var(--os-text)',
      }}>
        {win.noPadding
          ? <ErrorBoundaryOS windowId={win.id}><Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--os-muted)', fontSize: 12 }}>Loading...</div>}><Component {...(win.props ?? {})} /></Suspense></ErrorBoundaryOS>
          : (
            <div style={{ padding: '16px', minHeight: '100%', boxSizing: 'border-box' }}>
              <ErrorBoundaryOS windowId={win.id}><Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--os-muted)', fontSize: 12 }}>Loading...</div>}><Component {...(win.props ?? {})} /></Suspense></ErrorBoundaryOS>
            </div>
          )
        }
      </div>

      {/* 8-directional resize handles */}
      {!isMobile && !win.maximized && !win.pinned && RESIZE_HANDLES.map(h => (
        <div
          key={h.dir}
          data-resize-handle={h.dir}
          onPointerDown={(e) => onResizePointerDown(h.dir, e)}
          style={{ position: 'absolute', ...h.style, cursor: h.cursor, zIndex: 1, touchAction: 'none' }}
        />
      ))}

      {/* Edge snap preview overlay */}
      {snapPreview && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9989, pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute',
            top: snapPreview === 'max' ? 0 : 34,
            left: snapPreview === 'right' ? '50%' : 0,
            width: snapPreview === 'max' ? '100%' : '50%',
            height: snapPreview === 'max' ? '100%' : 'calc(100% - 34px)',
            border: '2px dashed var(--os-accent)',
            borderRadius: 8,
            background: 'var(--os-accent-bg)',
            opacity: 0.5,
            transition: 'all 0.15s ease',
          }} />
        </div>
      )}

      {/* Snap Zone Overlay */}
      <SnapZoneOverlay isDragging={isDragging} activeCorner={activeCorner} />

      {/* Context Menu */}
      {contextMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: contextMenu.x, top: contextMenu.y,
            background: 'var(--os-win-bg)', border: '1px solid var(--os-border)',
            borderRadius: '8px', boxShadow: 'var(--os-win-shadow)',
            backdropFilter: 'blur(20px)', zIndex: 10000,
            padding: '4px 0', minWidth: '160px',
          }}
        >
          <button
            onClick={() => handleContextAction('agent')}
            style={{
              width: '100%', padding: '8px 12px', textAlign: 'left',
              background: 'transparent', border: 'none',
              color: 'var(--os-text)', fontSize: '11px',
              cursor: 'pointer', borderRadius: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--os-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Agent ile aç
          </button>
          <button
            onClick={() => handleContextAction('summarize')}
            style={{
              width: '100%', padding: '8px 12px', textAlign: 'left',
              background: 'transparent', border: 'none',
              color: 'var(--os-text)', fontSize: '11px',
              cursor: 'pointer', borderRadius: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--os-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Özetle
          </button>
          <button
            onClick={() => handleContextAction('newWindow')}
            style={{
              width: '100%', padding: '8px 12px', textAlign: 'left',
              background: 'transparent', border: 'none',
              color: 'var(--os-text)', fontSize: '11px',
              cursor: 'pointer', borderRadius: 0,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--os-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Yeni pencere
          </button>
        </div>
      )}
    </div>
  )
}
